import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  InputAdornment,
  Alert,
  AlertTitle,
  Tooltip,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  alpha
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PrintIcon from "@mui/icons-material/Print";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CategoryIcon from "@mui/icons-material/Category";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import CallSplitRoundedIcon from "@mui/icons-material/CallSplitRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";

import ReconciliationService from "./services/ReconciliationService";
import StampDutyService from "./services/StampDutyService";
import SplitDepositModal from "../components/SplitDepositModal";
import DevMinWelSplitModal from "../components/DevMinWelSplitModal";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import PrintReportHeader from "../components/PrintReportHeader";
import { fNumber } from "../utils/formatNumber";

const ACCOUNTS = [
  { key: "shares", label: "Shares", color: "#0B3B60" },
  { key: "savings", label: "Savings", color: "#059669" },
  { key: "loan", label: "Loan", color: "#D97706" },
  { key: "ileya", label: "Ileya Savings", color: "#EA580C" },
  { key: "dev", label: "Development Levy", color: "#2563EB" },
  { key: "min", label: "Minutes", color: "#0284C7" },
  { key: "wel", label: "Welfare", color: "#7C3AED" },
  { key: "takaful", label: "Takaful", color: "#0E7490" },
  { key: "speacc", label: "Special Account", color: "#4F46E5" },
  { key: "business", label: "Business", color: "#0D9488" },
];

const EXTERNAL_DEDUCTION_CATEGORIES = [
  "Bank Charges & Maintenance / Stamp Duty",
  "Office Stationery & Secretariat Logistics",
  "Meeting Refreshments & Welfare",
  "Legal, Audit & Shura Retainers",
  "Cooperative Asset / Equipment Purchase",
  "Member Withdrawal Payout",
  "External Project / Investment Outflow",
  "Other External Purpose"
];

const EXTERNAL_CREDIT_CATEGORIES = [
  "Non-Interest Investment Return / Sukuk / Yield",
  "Direct Commercial Bank Credit / Surplus Recovery",
  "External Donation / Grant / Seed Capital",
  "Member External Direct Bank Deposit",
  "Asset Disposal / Liquidation Yield",
  "Other External Inflow"
];

export const OFFICIAL_MUTUAL_TRUST_ACCOUNTS = [
  { title: "Main Operations Account", number: "0018169614", category: "Core Operations", bank: "Jaiz Bank" },
  { title: "Business Partnership Account", number: "0018169676", category: "Trade & Commerce", bank: "Jaiz Bank" },
  { title: "Welfare & Emergency Relief", number: "0018169731", category: "Member Welfare", bank: "Jaiz Bank" },
  { title: "Target Savings Account", number: "0018169803", category: "Goal Savings", bank: "Jaiz Bank" },
];

export default function AccountReconciliation() {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const { user: currentUser } = auth;

  // Authorization check
  const storedUser = useMemo(() => {
    if (currentUser) return currentUser;
    try {
      const s = localStorage.getItem("user") || localStorage.getItem("profile");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  }, [currentUser]);

  const viewAsRole = sessionStorage.getItem("mtc_view_as_role") || "";

  const rolesString = useMemo(() => {
    if (viewAsRole) return viewAsRole.toLowerCase().replace(/role_/g, "");
    const u = storedUser || {};
    const parts = [];
    if (Array.isArray(u.roles)) parts.push(...u.roles.map((r) => (typeof r === "object" && r !== null ? (r.name || "") : String(r))));
    else if (u.roles) parts.push(typeof u.roles === "object" && u.roles !== null ? (u.roles.name || "") : String(u.roles));
    if (u.role) parts.push(Array.isArray(u.role) ? u.role.join(" ") : (typeof u.role === "object" && u.role !== null ? (u.role.name || "") : String(u.role)));
    if (u.roleName) parts.push(String(u.roleName));
    if (u.isAdmin) parts.push("admin");
    return parts.join(" ").toLowerCase().replace(/role_/g, "");
  }, [viewAsRole, storedUser]);

  const isAdmin = rolesString.includes("admin");
  const isPresident = rolesString.includes("president");
  const isFinancial = rolesString.includes("financial");
  const isFinancialII = rolesString.includes("financial_2");
  const isFinancialIII = rolesString.includes("financial_3");
  const isFinancialI =
    (isFinancial && !isFinancialII && !isFinancialIII) ||
    rolesString.includes("financial_1");
  const isSuperFinancial = isAdmin || isFinancialI || isPresident;
  const isRestrictedFinancial = (isFinancialII || isFinancialIII) && !isSuperFinancial;
  const isBusiness = rolesString.includes("business");
  const isRegistrar = rolesString.includes("registrar") || rolesString.includes("secretary");
  const canShowSplitTool = !isBusiness && !isPresident && !isRegistrar && (isAdmin || isFinancial);
  const canAccess = isAdmin || isFinancial || isPresident;
  const visibleAccounts = useMemo(() => {
    return canAccess ? ACCOUNTS : ACCOUNTS.filter((a) => a.key !== "business");
  }, [canAccess]);

  useEffect(() => {
    if (!canAccess) {
      navigate("/dashboard/app", { replace: true });
    }
  }, [canAccess, navigate]);

  // State
  const [entries, setEntries] = useState([]);
  const [metrics, setMetrics] = useState({ totalAdditions: 0, totalDeductions: 0, netAdjustment: 0, totalTransfersCount: 0, byAccount: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const getLast30DaysRange = () => {
    const now = new Date();
    const prior = new Date();
    prior.setDate(prior.getDate() - 30);
    return {
      startDate: prior.toISOString().slice(0, 10),
      endDate: now.toISOString().slice(0, 10),
    };
  };

  // Filter state (defaults to Last 30 Days)
  const [activePreset, setActivePreset] = useState("last30");
  const [accountFilter, setAccountFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [startDate, setStartDate] = useState(() => getLast30DaysRange().startDate);
  const [endDate, setEndDate] = useState(() => getLast30DaysRange().endDate);
  const [search, setSearch] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // New Reconciliation Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reconTab, setReconTab] = useState(0); // 0: Inter-Account Transfer, 1: External Purpose / Outside

  // Mode: Transfer fields
  const [formTransferFrom, setFormTransferFrom] = useState("shares");
  const [formTransferTo, setFormTransferTo] = useState("business");

  // Mode: External fields
  const [formAccount, setFormAccount] = useState("shares");
  const [formAction, setFormAction] = useState("subtract"); // subtract (deduct) vs add (credit)
  const [formExternalCategory, setFormExternalCategory] = useState(EXTERNAL_DEDUCTION_CATEGORIES[0]);

  // Shared fields
  const [formAmount, setFormAmount] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formDate, setFormDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [formNotes, setFormNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState("");

  // Delete Confirm Dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Top-Level Module Navigation: 0 = Transfers & External Reconciliations, 1 = Monthly Bank Statement Balancing, 2 = Stamp Duty Deductions & Waivers
  const [mainPageTab, setMainPageTab] = useState(() => (isRestrictedFinancial ? 2 : 0));

  useEffect(() => {
    if (isRestrictedFinancial && mainPageTab !== 2) {
      setMainPageTab(2);
    }
  }, [isRestrictedFinancial, mainPageTab]);

  // Bank Statement Reconciliation State
  const [bankCycleYear, setBankCycleYear] = useState(() => new Date().getFullYear());
  const [bankCycleMonth, setBankCycleMonth] = useState(() => new Date().getMonth() + 1);
  const [bankSummaryData, setBankSummaryData] = useState(null);
  const [loadingBankSummary, setLoadingBankSummary] = useState(false);
  const [bankInputCredits, setBankInputCredits] = useState("");
  const [bankInputDebits, setBankInputDebits] = useState("");
  const [bankOpeningBal, setBankOpeningBal] = useState("");
  const [bankClosingBal, setBankClosingBal] = useState("");
  const [selectedOfficialAcct, setSelectedOfficialAcct] = useState("0018169614");
  const [bankName, setBankName] = useState("Jaiz Bank");
  const [bankAccountNumber, setBankAccountNumber] = useState("0018169614");
  const [statementRef, setStatementRef] = useState("");
  const [bankReconNotes, setBankReconNotes] = useState("");
  const [savingBankRecon, setSavingBankRecon] = useState(false);
  const [bankReconSuccess, setBankReconSuccess] = useState("");
  const [bankReconError, setBankReconError] = useState("");
  const [pastBankRecons, setPastBankRecons] = useState([]);

  // Stamp Duty Overview State
  const [stampSummary, setStampSummary] = useState({
    totalStampDutyDeducted: 0,
    totalDepositVolume: 0,
    totalChargedCount: 0,
    totalWaivedCount: 0,
    totalWaivedPotentialAmount: 0,
    accountBreakdown: {}
  });
  const [stampRecords, setStampRecords] = useState([]);
  const [loadingStamp, setLoadingStamp] = useState(false);
  const [stampAccountFilter, setStampAccountFilter] = useState("all");
  const [stampWaivedFilter, setStampWaivedFilter] = useState("all");
  const [stampSearch, setStampSearch] = useState("");
  const [splitDepositOpen, setSplitDepositOpen] = useState(false);
  const [devMinWelSplitOpen, setDevMinWelSplitOpen] = useState(false);

  // Fetch monthly bank cycle summary
  const fetchBankCycleSummary = useCallback(async (y, m) => {
    setLoadingBankSummary(true);
    setBankReconError("");
    try {
      const res = await ReconciliationService.getMonthlyCycleSummary({ year: y, month: m });
      const data = res?.data || {};
      setBankSummaryData(data);
      if (data.existingReconciliation) {
        const er = data.existingReconciliation;
        setBankInputCredits(String(er.bankStatementCredits || ""));
        setBankInputDebits(String(er.bankStatementDebits || ""));
        setBankOpeningBal(String(er.bankOpeningBalance || ""));
        setBankClosingBal(String(er.bankClosingBalance || ""));
        const recBankName = er.bankName || "Jaiz Bank";
        const recAcctNum = er.bankAccountNumber || "";
        setBankName(recBankName);
        setBankAccountNumber(recAcctNum);
        const matchAcct = OFFICIAL_MUTUAL_TRUST_ACCOUNTS.find(a => a.number === recAcctNum);
        setSelectedOfficialAcct(matchAcct ? matchAcct.number : (recAcctNum ? "custom" : "0018169614"));
        setStatementRef(er.statementReference || "");
        setBankReconNotes(er.notes || "");
      } else {
        setBankInputCredits("");
        setBankInputDebits("");
        setBankReconNotes("");
      }
    } catch (err) {
      console.error("fetchBankCycleSummary err:", err);
      setBankReconError(err?.response?.data?.error || err.message || "Failed to load cycle figures.");
    } finally {
      setLoadingBankSummary(false);
    }
  }, []);

  const loadPastBankRecons = useCallback(async () => {
    try {
      const res = await ReconciliationService.getBankReconciliations();
      setPastBankRecons(res?.data || []);
    } catch (err) {
      console.error("loadPastBankRecons err:", err);
    }
  }, []);

  // Fetch stamp duty summary
  const loadStampDuty = useCallback(async () => {
    setLoadingStamp(true);
    try {
      const params = {};
      if (stampAccountFilter !== "all") params.accountType = stampAccountFilter;
      if (stampWaivedFilter !== "all") params.isWaived = stampWaivedFilter;
      if (stampSearch.trim()) params.search = stampSearch.trim();

      const res = await StampDutyService.getSummary(params);
      const data = res?.data || {};
      setStampSummary(data.summary || {
        totalStampDutyDeducted: 0,
        totalDepositVolume: 0,
        totalChargedCount: 0,
        totalWaivedCount: 0,
        totalWaivedPotentialAmount: 0,
        accountBreakdown: {}
      });
      setStampRecords(data.records || []);
    } catch (err) {
      console.error("loadStampDuty err:", err);
    } finally {
      setLoadingStamp(false);
    }
  }, [stampAccountFilter, stampWaivedFilter, stampSearch]);

  useEffect(() => {
    if (mainPageTab === 1) {
      fetchBankCycleSummary(bankCycleYear, bankCycleMonth);
      loadPastBankRecons();
    } else if (mainPageTab === 2) {
      loadStampDuty();
    }
  }, [mainPageTab, bankCycleYear, bankCycleMonth, fetchBankCycleSummary, loadPastBankRecons, loadStampDuty]);

  const handleSaveBankReconciliation = async () => {
    setBankReconError("");
    setBankReconSuccess("");
    const bCredits = Number(String(bankInputCredits).replace(/,/g, ""));
    const bDebits = Number(String(bankInputDebits).replace(/,/g, ""));

    if (isNaN(bCredits) || isNaN(bDebits)) {
      setBankReconError("Please enter valid numeric figures for bank statement credits and debits.");
      return;
    }

    setSavingBankRecon(true);
    try {
      const sysCredits = bankSummaryData?.systemFigures?.systemCredits || 0;
      const sysDebits = bankSummaryData?.systemFigures?.systemDebits || 0;

      const payload = {
        year: bankCycleYear,
        month: bankCycleMonth,
        cyclePeriod: bankSummaryData?.cycle?.cyclePeriod || `Month ${bankCycleMonth} ${bankCycleYear}`,
        startDate: bankSummaryData?.cycle?.startDate,
        endDate: bankSummaryData?.cycle?.endDate,
        bankName,
        bankAccountNumber,
        statementReference: statementRef,
        bankStatementCredits: bCredits,
        bankStatementDebits: bDebits,
        bankOpeningBalance: Number(String(bankOpeningBal).replace(/,/g, "")) || 0,
        bankClosingBalance: Number(String(bankClosingBal).replace(/,/g, "")) || 0,
        systemCredits: sysCredits,
        systemDebits: sysDebits,
        notes: bankReconNotes
      };

      const res = await ReconciliationService.saveBankReconciliation(payload);
      setBankReconSuccess(res?.data?.message || "Monthly Bank Statement Reconciliation saved successfully!");
      fetchBankCycleSummary(bankCycleYear, bankCycleMonth);
      loadPastBankRecons();
      setTimeout(() => setBankReconSuccess(""), 6000);
    } catch (err) {
      console.error("handleSaveBankReconciliation err:", err);
      setBankReconError(err?.response?.data?.error || err.message || "Failed to save bank reconciliation.");
    } finally {
      setSavingBankRecon(false);
    }
  };

  // Switch action and category synchronously to prevent out-of-range Select warning
  const handleActionChange = (newAction) => {
    setFormAction(newAction);
    setFormExternalCategory(
      newAction === "subtract" ? EXTERNAL_DEDUCTION_CATEGORIES[0] : EXTERNAL_CREDIT_CATEGORIES[0]
    );
  };

  // Load entries
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (accountFilter !== "all") params.accountType = accountFilter;
      if (actionFilter !== "all") params.action = actionFilter;
      if (typeFilter !== "all") params.reconciliationType = typeFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (search.trim()) params.search = search.trim();

      const res = await ReconciliationService.getAll(params);
      const data = res.data || {};
      setEntries(data.entries || []);
      setMetrics(data.metrics || { totalAdditions: 0, totalDeductions: 0, netAdjustment: 0, totalTransfersCount: 0, byAccount: {} });
    } catch (err) {
      console.error("Error loading reconciliation entries:", err);
      setError(err?.response?.data?.error || err.message || "Failed to load reconciliations.");
    } finally {
      setLoading(false);
    }
  }, [accountFilter, actionFilter, typeFilter, startDate, endDate, search]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calendar Presets
  const setPresetRange = (preset) => {
    setActivePreset(preset);
    const now = new Date();
    if (preset === "all") {
      setStartDate("");
      setEndDate("");
    } else if (preset === "thisMonth") {
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const lastDay = new Date(year, now.getMonth() + 1, 0).getDate();
      setStartDate(`${year}-${month}-01`);
      setEndDate(`${year}-${month}-${String(lastDay).padStart(2, "0")}`);
    } else if (preset === "last30") {
      const prior = new Date();
      prior.setDate(prior.getDate() - 30);
      setStartDate(prior.toISOString().slice(0, 10));
      setEndDate(now.toISOString().slice(0, 10));
    } else if (preset === "thisYear") {
      const year = now.getFullYear();
      setStartDate(`${year}-01-01`);
      setEndDate(now.toISOString().slice(0, 10));
    }
    setPage(0);
  };

  // Submit New Reconciliation
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setDialogError("");

    const numAmt = parseFloat(String(formAmount).replace(/,/g, ""));
    if (!Number.isFinite(numAmt) || numAmt <= 0) {
      setDialogError("Please enter a valid amount greater than zero.");
      return;
    }

    if (!formDescription.trim()) {
      setDialogError("Please enter a clear purpose/narration for this transaction.");
      return;
    }

    if (!formDate) {
      setDialogError("Please select the date when the transaction was executed.");
      return;
    }

    // Inter-Account Transfer validation
    if (reconTab === 0) {
      if (formTransferFrom === formTransferTo) {
        setDialogError("Source and Destination accounts cannot be the same. Please select different accounts.");
        return;
      }
    }

    setSubmitting(true);
    try {
      if (reconTab === 0) {
        // Mode 1: Inter-Account Transfer
        await ReconciliationService.create({
          reconciliationType: "transfer",
          fromAccount: formTransferFrom,
          toAccount: formTransferTo,
          amount: numAmt,
          description: formDescription.trim(),
          transactionDate: formDate,
          notes: formNotes.trim(),
        });
        const fromLabel = ACCOUNTS.find((a) => a.key === formTransferFrom)?.label || formTransferFrom;
        const toLabel = ACCOUNTS.find((a) => a.key === formTransferTo)?.label || formTransferTo;
        setSuccessMsg(`Inter-account transfer of ₦${fNumber(numAmt)} from ${fromLabel} to ${toLabel} recorded successfully!`);
      } else {
        // Mode 2: External Purpose / Outside transaction
        await ReconciliationService.create({
          reconciliationType: formAction === "add" ? "external_inflow" : "external_outflow",
          accountType: formAccount,
          action: formAction,
          externalCategory: formExternalCategory,
          amount: numAmt,
          description: formDescription.trim(),
          transactionDate: formDate,
          notes: formNotes.trim(),
        });
        const accLabel = ACCOUNTS.find((a) => a.key === formAccount)?.label || formAccount;
        setSuccessMsg(`External reconciliation (${formAction === "add" ? "+ Credit" : "- Deduction"}) for ${accLabel} recorded successfully!`);
      }

      setDialogOpen(false);
      // Reset form
      setFormAmount("");
      setFormDescription("");
      setFormNotes("");
      setFormDate(new Date().toISOString().slice(0, 10));
      loadData();
      setTimeout(() => setSuccessMsg(""), 6000);
    } catch (err) {
      console.error("Create reconciliation error:", err);
      setDialogError(err?.response?.data?.error || err.message || "Failed to record reconciliation entry.");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete / Revert Confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await ReconciliationService.remove(deleteTarget._id || deleteTarget.id);
      if (deleteTarget.transferGroupId) {
        setSuccessMsg(`Inter-Account Transfer group ${deleteTarget.transferGroupId} was reverted. Both debit and credit legs have been removed.`);
      } else {
        setSuccessMsg(`Reconciliation entry ${deleteTarget.referenceNo || ""} reverted successfully.`);
      }
      setDeleteTarget(null);
      loadData();
      setTimeout(() => setSuccessMsg(""), 6000);
    } catch (err) {
      console.error("Delete reconciliation error:", err);
      setError(err?.response?.data?.error || err.message || "Failed to delete reconciliation entry.");
    } finally {
      setDeleting(false);
    }
  };

  // Print State & Handlers
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    const beforePrint = () => setIsPrinting(true);
    const afterPrint = () => setIsPrinting(false);
    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);
    return () => {
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, []);

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setIsPrinting(false), 1000);
    }, 150);
  };

  // Quick Amount preset helpers
  const setQuickAmount = (val) => {
    setFormAmount(String(val));
  };

  // Table pagination slice (expands to all entries during printing)
  const paginatedEntries = useMemo(() => {
    if (isPrinting) return entries;
    const start = page * rowsPerPage;
    return entries.slice(start, start + rowsPerPage);
  }, [entries, page, rowsPerPage, isPrinting]);

  if (!canAccess) {
    return null;
  }

  const fromAccObj = ACCOUNTS.find((a) => a.key === formTransferFrom) || { label: formTransferFrom, color: "#0B3B60" };
  const toAccObj = ACCOUNTS.find((a) => a.key === formTransferTo) || { label: formTransferTo, color: "#0D9488" };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Official Print Statement Header (Visible only during printing) */}
      <PrintReportHeader
        title={
          mainPageTab === 0
            ? "ACCOUNT RECONCILIATION & AUDIT TRAIL STATEMENT"
            : mainPageTab === 1
            ? "MONTHLY BANK STATEMENT BALANCING LEDGER"
            : "STAMP DUTY DEDUCTIONS & WAIVERS AUDIT"
        }
        subtitle="Official financial double-entry transaction and audit statement"
        period={
          startDate && endDate
            ? `${startDate} to ${endDate}`
            : startDate
            ? `From ${startDate}`
            : endDate
            ? `Up to ${endDate}`
            : "All Time"
        }
        account={accountFilter === "all" ? "All Accounts" : (ACCOUNTS.find((a) => a.key === accountFilter)?.label || accountFilter)}
        officer={
          currentUser
            ? `${currentUser.firstName || ''} ${currentUser.lastName || ''} (${currentUser.username || 'Officer'})`.trim()
            : "Financial Officer"
        }
        metrics={[
          { label: "Total Inflows / Credits", value: `+₦${fNumber(metrics.totalAdditions)}`, color: "#059669" },
          { label: "Total Outflows / Debits", value: `-₦${fNumber(metrics.totalDeductions)}`, color: "#DC2626" },
          { label: "Net Adjustment", value: `${metrics.netAdjustment < 0 ? '-' : '+'}₦${fNumber(Math.abs(metrics.netAdjustment))}`, color: metrics.netAdjustment >= 0 ? "#059669" : "#DC2626" },
          { label: "Total Records", value: `${entries.length}`, color: "#0B3B60" }
        ]}
      />

      {/* Top Header */}
      <Box className="no-print" sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, flexWrap: "wrap", gap: 2, mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <IconButton onClick={() => navigate("/dashboard/app")} sx={{ border: "1px solid #CBD5E1", borderRadius: 2 }}>
              <ArrowBackIcon sx={{ color: "#0B3B60" }} />
            </IconButton>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#0B3B60", letterSpacing: "-0.02em", fontSize: { xs: "1.4rem", sm: "1.75rem", md: "2rem" } }}>
                Account Reconciliation
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748B" }}>
                Seamless inter-account transfers (e.g. Shares to Business capitalisation) & external purpose reconciliation (stationeries, bank charges, returns) with complete double-entry audit trail.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              Print Statement
            </Button>

            {(isFinancialI || isAdmin) && (
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={(e) => {
                  e.currentTarget.blur();
                  setDialogError("");
                  setDialogOpen(true);
                }}
                sx={{
                  backgroundColor: "#0B3B60",
                  "&:hover": { backgroundColor: "#072740" },
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 2.5,
                  boxShadow: "0 4px 14px rgba(11, 59, 96, 0.25)"
                }}
              >
                New Adjustment / Transfer
              </Button>
            )}
          </Box>
        </Box>

        {successMsg && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccessMsg("")}>
            {successMsg}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>

      {/* TOP LEVEL MODULE NAVIGATION TABS */}
      <Paper className="no-print" elevation={0} sx={{ mb: 3, borderRadius: 2.5, border: "1px solid #CBD5E1", backgroundColor: "#ffffff", overflow: "hidden" }}>
        <Tabs
          value={mainPageTab}
          onChange={(e, val) => setMainPageTab(val)}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 2,
            backgroundColor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
            "& .MuiTab-root": {
              fontWeight: 800,
              fontSize: "0.92rem",
              textTransform: "none",
              minHeight: 52,
              gap: 1
            }
          }}
        >
          {isSuperFinancial && (
            <Tab value={0} icon={<CompareArrowsIcon />} iconPosition="start" label="Inter-Account & External Reconciliations" />
          )}
          {isSuperFinancial && (
            <Tab value={1} icon={<AccountBalanceIcon />} iconPosition="start" label="Monthly Bank Statement Balancing" />
          )}
          <Tab value={2} icon={<VerifiedUserRoundedIcon />} iconPosition="start" label={`Stamp Duty Deductions & Waivers (₦${fNumber(stampSummary?.totalStampDutyDeducted || 0)})`} />
        </Tabs>
      </Paper>

      {/* ========================================================================= */}
      {/* TAB 0: INTER-ACCOUNT & EXTERNAL RECONCILIATIONS LEDGER                   */}
      {/* ========================================================================= */}
      {mainPageTab === 0 && (
        <>
          {/* KPI Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }} className="no-print">
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2.25,
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              borderTop: "4px solid #0B3B60",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, textTransform: "uppercase" }}>
              Net External Adjustment
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: metrics.netAdjustment >= 0 ? "#059669" : "#DC2626", mt: 0.5 }}>
              {metrics.netAdjustment < 0 ? "-" : "+"}₦{fNumber(Math.abs(metrics.netAdjustment))}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748B" }}>
              Net external funds impact on cash position
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2.25,
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              borderTop: "4px solid #059669",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, textTransform: "uppercase" }}>
              Total Inflows / Credits
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#059669", mt: 0.5 }}>
              +₦{fNumber(metrics.totalAdditions)}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748B" }}>
              Bank deposits, returns & transfers in
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2.25,
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              borderTop: "4px solid #DC2626",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, textTransform: "uppercase" }}>
              Total Disbursements / Deductions
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#DC2626", mt: 0.5 }}>
              -₦{fNumber(metrics.totalDeductions)}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748B" }}>
              Bank charges, stationeries & transfers out
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2.25,
              borderRadius: 3,
              border: "1px solid #E2E8F0",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              borderTop: "4px solid #2563EB",
              backgroundColor: "#FFFFFF",
            }}
          >
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, textTransform: "uppercase" }}>
              Inter-Account Transfers
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#2563EB", mt: 0.5 }}>
              {metrics.totalTransfersCount || 0} Transfer{metrics.totalTransfersCount === 1 ? "" : "s"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#64748B" }}>
              Balanced fund shifts between accounts
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Account Breakdown Chips */}
      <Box className="no-print" sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: "#334155", mr: 1, textTransform: "uppercase" }}>
          Account Impact:
        </Typography>
        {visibleAccounts.map((acc) => {
          const accData = metrics.byAccount?.[acc.key] || { net: 0 };
          const net = accData.net || 0;
          return (
            <Chip
              key={acc.key}
              label={`${acc.label}: ${net < 0 ? "-" : "+"}₦${fNumber(Math.abs(net))}`}
              size="small"
              onClick={() => {
                setAccountFilter(accountFilter === acc.key ? "all" : acc.key);
                setPage(0);
              }}
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                cursor: "pointer",
                backgroundColor: accountFilter === acc.key ? acc.color : alpha(acc.color, 0.08),
                color: accountFilter === acc.key ? "#FFFFFF" : acc.color,
                border: `1px solid ${accountFilter === acc.key ? acc.color : alpha(acc.color, 0.3)}`,
                "&:hover": {
                  backgroundColor: accountFilter === acc.key ? acc.color : alpha(acc.color, 0.18),
                },
              }}
            />
          );
        })}
      </Box>

      {/* Filter and Search Bar */}
      <Paper className="no-print" sx={{ p: 2, mb: 3, borderRadius: 3, border: "1px solid #E2E8F0", backgroundColor: "#FAFAFA" }}>
        <Grid container spacing={2} alignItems="center">
          {/* Search Field */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search narration, ref, voucher, category..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#64748B", fontSize: "1.2rem" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Account Filter */}
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="account-filter-label">Account</InputLabel>
              <Select
                labelId="account-filter-label"
                label="Account"
                value={accountFilter}
                onChange={(e) => {
                  setAccountFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Accounts</MenuItem>
                {visibleAccounts.map((a) => (
                  <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Nature / Type Filter */}
          <Grid item xs={6} sm={3} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="type-filter-label">Nature</InputLabel>
              <Select
                labelId="type-filter-label"
                label="Nature"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Natures</MenuItem>
                <MenuItem value="transfer">🔄 Transfers</MenuItem>
                <MenuItem value="external">External (Outside)</MenuItem>
                <MenuItem value="external_outflow">📤 Outflows (-)</MenuItem>
                <MenuItem value="external_inflow">📥 Inflows (+)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Action Filter */}
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel id="action-filter-label">Action</InputLabel>
              <Select
                labelId="action-filter-label"
                label="Action"
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Actions</MenuItem>
                <MenuItem value="add">+ Credit</MenuItem>
                <MenuItem value="subtract">- Debit</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Calendar Range Filter */}
          <Grid item xs={12} sm={9} md={3}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <TextField
                size="small"
                label="From"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setActivePreset("custom");
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="To"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setActivePreset("custom");
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Grid>

          {/* Quick Calendar Range Presets */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", pt: 1, borderTop: "1px dashed #E2E8F0" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", display: "flex", alignItems: "center", gap: 0.5 }}>
                <CalendarMonthIcon fontSize="inherit" /> Date Range Presets:
              </Typography>
              <Button
                size="small"
                variant={activePreset === "last30" ? "contained" : "outlined"}
                onClick={() => setPresetRange("last30")}
                sx={{
                  py: 0.2,
                  px: 1,
                  fontSize: "0.75rem",
                  borderRadius: 1.5,
                  backgroundColor: activePreset === "last30" ? "#0B3B60" : "transparent",
                  color: activePreset === "last30" ? "#FFFFFF" : "#0B3B60",
                }}
              >
                Last 30 Days
              </Button>
              <Button
                size="small"
                variant={activePreset === "thisMonth" ? "contained" : "outlined"}
                onClick={() => setPresetRange("thisMonth")}
                sx={{
                  py: 0.2,
                  px: 1,
                  fontSize: "0.75rem",
                  borderRadius: 1.5,
                  backgroundColor: activePreset === "thisMonth" ? "#0B3B60" : "transparent",
                  color: activePreset === "thisMonth" ? "#FFFFFF" : "#0B3B60",
                }}
              >
                This Month
              </Button>
              <Button
                size="small"
                variant={activePreset === "thisYear" ? "contained" : "outlined"}
                onClick={() => setPresetRange("thisYear")}
                sx={{
                  py: 0.2,
                  px: 1,
                  fontSize: "0.75rem",
                  borderRadius: 1.5,
                  backgroundColor: activePreset === "thisYear" ? "#0B3B60" : "transparent",
                  color: activePreset === "thisYear" ? "#FFFFFF" : "#0B3B60",
                }}
              >
                This Year
              </Button>
              <Button
                size="small"
                variant={activePreset === "all" ? "contained" : "outlined"}
                onClick={() => setPresetRange("all")}
                sx={{
                  py: 0.2,
                  px: 1,
                  fontSize: "0.75rem",
                  borderRadius: 1.5,
                  backgroundColor: activePreset === "all" ? "#0B3B60" : "transparent",
                  color: activePreset === "all" ? "#FFFFFF" : "#0B3B60",
                }}
              >
                All Time
              </Button>

              {(startDate || endDate || accountFilter !== "all" || actionFilter !== "all" || typeFilter !== "all" || search) && (
                <Button
                  size="small"
                  variant="text"
                  color="error"
                  onClick={() => {
                    setActivePreset("all");
                    setStartDate("");
                    setEndDate("");
                    setAccountFilter("all");
                    setActionFilter("all");
                    setTypeFilter("all");
                    setSearch("");
                    setPage(0);
                  }}
                  sx={{ ml: "auto", textTransform: "none", fontSize: "0.75rem", fontWeight: 700 }}
                >
                  Clear All Filters
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Table of Reconciliation Entries with Smooth Mobile Horizontal Scrolling */}
      <Paper sx={{ borderRadius: 3, border: "1px solid #E2E8F0", boxShadow: "0 4px 12px rgba(0,0,0,0.03)", overflow: "hidden" }}>
        <TableContainer sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch", width: "100%" }}>
          <Table sx={{ minWidth: { xs: 850, sm: 950 } }}>
            <TableHead sx={{ backgroundColor: "#0B3B60" }}>
              <TableRow>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "0.8125rem", py: 1.8 }}>Date of Transaction</TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "0.8125rem", py: 1.8 }}>Logged Timestamp</TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "0.8125rem", py: 1.8 }}>Account</TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "0.8125rem", py: 1.8 }}>Nature / Type</TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "0.8125rem", py: 1.8 }} align="right">Amount (₦)</TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "0.8125rem", py: 1.8 }}>Transaction Purpose / Description</TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "0.8125rem", py: 1.8 }}>Officer</TableCell>
                {(isFinancialI || isAdmin) && (
                  <TableCell className="print-hide-action" sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "0.8125rem", py: 1.8 }} align="center">Action</TableCell>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} sx={{ color: "#0B3B60", mb: 1.5 }} />
                    <Typography variant="body2" sx={{ color: "#64748B" }}>
                      Loading reconciliation entries...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#334155", mb: 0.5 }}>
                      No reconciliation records found
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#64748B", mb: 2 }}>
                      No adjustment entries match your current search or date range filter.
                    </Typography>
                    {(isFinancialI || isAdmin) && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={(e) => {
                          e.currentTarget.blur();
                          setDialogOpen(true);
                        }}
                        sx={{ backgroundColor: "#0B3B60", textTransform: "none", fontWeight: 700 }}
                      >
                        Record Adjustment or Transfer
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedEntries.map((row) => {
                  const isAdd = row.action === "add";
                  const isTransfer = row.reconciliationType === "transfer" || Boolean(row.transferGroupId);
                  const counterpartObj = row.counterpartAccount ? ACCOUNTS.find((a) => a.key === row.counterpartAccount) : null;
                  const accObj = ACCOUNTS.find((a) => a.key === row.accountType) || { label: row.accountType, color: "#0B3B60" };
                  const tDate = row.transactionDate ? new Date(row.transactionDate).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "-";
                  const cTimestamp = row.createdAt ? new Date(row.createdAt).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

                  return (
                    <TableRow
                      key={row._id || row.id || row.referenceNo}
                      sx={{
                        "&:hover": { backgroundColor: "#F8FAFC" },
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      {/* Transaction Date */}
                      <TableCell sx={{ fontWeight: 700, color: "#0F172A", whiteSpace: "nowrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                          <CalendarMonthIcon sx={{ fontSize: "1rem", color: "#64748B" }} />
                          <span>{tDate}</span>
                        </Box>
                      </TableCell>

                      {/* Logged Entry Timestamp */}
                      <TableCell sx={{ color: "#64748B", fontSize: "0.8125rem", whiteSpace: "nowrap" }}>
                        {cTimestamp}
                      </TableCell>

                      {/* Account */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={accObj.label}
                          sx={{
                            backgroundColor: alpha(accObj.color, 0.1),
                            color: accObj.color,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            border: `1px solid ${alpha(accObj.color, 0.25)}`,
                          }}
                        />
                      </TableCell>

                      {/* Nature / Type Badge */}
                      <TableCell>
                        {isTransfer ? (
                          <Box>
                            <Chip
                              size="small"
                              icon={<SwapHorizIcon fontSize="small" />}
                              label={isAdd ? `Transfer ⬅ ${counterpartObj?.label || row.counterpartAccount}` : `Transfer ➔ ${counterpartObj?.label || row.counterpartAccount}`}
                              sx={{
                                backgroundColor: isAdd ? "#EFF6FF" : "#FEF3C7",
                                color: isAdd ? "#1D4ED8" : "#B45309",
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                border: `1px solid ${isAdd ? "#BFDBFE" : "#FDE68A"}`,
                                "& .MuiChip-icon": { color: isAdd ? "#1D4ED8" : "#B45309" }
                              }}
                            />
                            {row.transferGroupId && (
                              <Typography variant="caption" sx={{ color: "#64748B", display: "block", fontSize: "0.6875rem", mt: 0.3 }}>
                                Linked: {row.transferGroupId}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Box>
                            <Chip
                              size="small"
                              icon={isAdd ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
                              label={isAdd ? "+ Credit / Inflow" : "- Deduct / Outflow"}
                              sx={{
                                backgroundColor: isAdd ? "#ECFDF5" : "#FEF2F2",
                                color: isAdd ? "#047857" : "#B91C1C",
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                border: `1px solid ${isAdd ? "#A7F3D0" : "#FECACA"}`,
                                "& .MuiChip-icon": { color: isAdd ? "#047857" : "#B91C1C" },
                              }}
                            />
                            {row.externalCategory && (
                              <Typography variant="caption" sx={{ color: "#475569", display: "block", fontWeight: 600, fontSize: "0.7rem", mt: 0.4 }}>
                                🏷️ {row.externalCategory}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>

                      {/* Amount */}
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: "0.95rem", color: isAdd ? "#059669" : "#DC2626", whiteSpace: "nowrap" }}>
                        {isAdd ? "+" : "-"}₦{fNumber(row.amount)}
                      </TableCell>

                      {/* Description / Purpose */}
                      <TableCell sx={{ maxWidth: 340 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#1E293B" }}>
                          {row.description}
                        </Typography>
                        {row.referenceNo && (
                          <Typography variant="caption" sx={{ color: "#94A3B8", display: "block" }}>
                            Ref: {row.referenceNo}
                          </Typography>
                        )}
                        {row.notes && (
                          <Typography variant="caption" sx={{ color: "#64748B", display: "block", fontStyle: "italic" }}>
                            Note: {row.notes}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Officer */}
                      <TableCell sx={{ color: "#334155", fontSize: "0.8125rem", whiteSpace: "nowrap" }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.performerName || "Financial Officer"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748B" }}>
                          {row.performerRole || "Financial I"}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      {(isFinancialI || isAdmin) && (
                        <TableCell align="center" className="print-hide-action">
                          <Tooltip title={isTransfer ? "Revert entire inter-account transfer (both legs)" : "Delete / Revert this adjustment"}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.currentTarget.blur();
                                setDeleteTarget(row);
                              }}
                              sx={{
                                color: "#EF4444",
                                "&:hover": { backgroundColor: "#FEE2E2" },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          className="no-print"
          component="div"
          count={entries.length}
          page={page}
          onPageChange={(e, newP) => setPage(newP)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>
    </>
  )}

      {/* ========================================================================= */}
      {/* TAB 1: MONTHLY BANK STATEMENT BALANCING (FINANCIAL I & ADMIN)             */}
      {/* ========================================================================= */}
      {mainPageTab === 1 && (
        <Box>
          {/* Cycle Selection & Header Card */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 2.5,
              border: "1px solid #CBD5E1",
              backgroundColor: "#ffffff",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarMonthIcon sx={{ color: "#0B3B60" }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#1E293B" }}>
                  Accounting Cycle Period:
                </Typography>
              </Box>

              <TextField
                select
                size="small"
                label="Month"
                value={bankCycleMonth}
                onChange={(e) => setBankCycleMonth(Number(e.target.value))}
                SelectProps={{ native: true }}
                sx={{ width: 140 }}
              >
                {[
                  { m: 1, name: "January" },
                  { m: 2, name: "February" },
                  { m: 3, name: "March" },
                  { m: 4, name: "April" },
                  { m: 5, name: "May" },
                  { m: 6, name: "June" },
                  { m: 7, name: "July" },
                  { m: 8, name: "August" },
                  { m: 9, name: "September" },
                  { m: 10, name: "October" },
                  { m: 11, name: "November" },
                  { m: 12, name: "December" },
                ].map(({ m, name }) => (
                  <option key={m} value={m}>{name}</option>
                ))}
              </TextField>

              <TextField
                size="small"
                label="Year"
                type="number"
                value={bankCycleYear}
                onChange={(e) => setBankCycleYear(Number(e.target.value))}
                sx={{ width: 110 }}
              />

              <Button
                variant="outlined"
                size="small"
                onClick={() => fetchBankCycleSummary(bankCycleYear, bankCycleMonth)}
                startIcon={<FactCheckRoundedIcon />}
                disabled={loadingBankSummary}
                sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
              >
                {loadingBankSummary ? "Computing..." : "Fetch Cycle Figures"}
              </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={`Cycle: ${bankSummaryData?.cycle?.cyclePeriod || `Month ${bankCycleMonth} ${bankCycleYear}`}`}
                sx={{ fontWeight: 800, backgroundColor: "#EFF6FF", color: "#1E40AF" }}
              />
            </Box>
          </Paper>

          {bankReconError && <Alert severity="error" sx={{ mb: 2.5 }}>{bankReconError}</Alert>}
          {bankReconSuccess && <Alert severity="success" sx={{ mb: 2.5 }}>{bankReconSuccess}</Alert>}

          {loadingBankSummary ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#0B3B60" }} />
            </Box>
          ) : (
            <>
              {/* STATUS BALANCING BANNER */}
              {(() => {
                const sysCredits = bankSummaryData?.systemFigures?.systemCredits || 0;
                const sysDebits = bankSummaryData?.systemFigures?.systemDebits || 0;
                const bCredits = Number(String(bankInputCredits).replace(/,/g, "")) || 0;
                const bDebits = Number(String(bankInputDebits).replace(/,/g, "")) || 0;
                const creditVariance = Number((sysCredits - bCredits).toFixed(2));
                const debitVariance = Number((sysDebits - bDebits).toFixed(2));
                const isMatched = Math.abs(creditVariance) === 0 && Math.abs(debitVariance) === 0 && bCredits > 0;

                return (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 3,
                      background: isMatched
                        ? "linear-gradient(135deg, #065F46 0%, #047857 100%)"
                        : bCredits > 0
                        ? "linear-gradient(135deg, #991B1B 0%, #B91C1C 100%)"
                        : "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
                      color: "#ffffff",
                      boxShadow: "0 10px 25px -5px rgba(0,0,0,0.15)"
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        {isMatched ? (
                          <CheckCircleRoundedIcon sx={{ fontSize: 44, color: "#34D399" }} />
                        ) : bCredits > 0 ? (
                          <WarningRoundedIcon sx={{ fontSize: 44, color: "#FCD34D" }} />
                        ) : (
                          <FactCheckRoundedIcon sx={{ fontSize: 44, color: "#93C5FD" }} />
                        )}
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            {isMatched
                              ? "ACCOUNTS PERFECTLY BALANCED & RECONCILED (₦0.00 Variance)"
                              : bCredits > 0
                              ? "VARIANCE DETECTED BETWEEN SYSTEM & BANK STATEMENT"
                              : "ENTER BANK STATEMENT CREDITS & DEBITS TO BEGIN RECONCILIATION"}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                            {isMatched
                              ? "All internal system records match the bank statement credits and debits precisely."
                              : bCredits > 0
                              ? `Credit Variance: ₦${fNumber(creditVariance)} • Debit Variance: ₦${fNumber(debitVariance)}`
                              : "Input the total credits (inflows) and debits (outflows) from the official commercial bank statement."}
                          </Typography>
                        </Box>
                      </Box>

                      {bCredits > 0 && (
                        <Chip
                          label={isMatched ? "BALANCED" : "VARIANCE NOTED"}
                          sx={{
                            backgroundColor: isMatched ? "#34D399" : "#FCD34D",
                            color: "#0F172A",
                            fontWeight: 900,
                            fontSize: "0.85rem",
                            px: 1.5,
                            py: 0.5
                          }}
                        />
                      )}
                    </Box>
                  </Paper>
                );
              })()}

              {/* 6 COMPARISON KPI CARDS (SYSTEM VS BANK) */}
              {(() => {
                const sysCredits = bankSummaryData?.systemFigures?.systemCredits || 0;
                const sysDebits = bankSummaryData?.systemFigures?.systemDebits || 0;
                const bCredits = Number(String(bankInputCredits).replace(/,/g, "")) || 0;
                const bDebits = Number(String(bankInputDebits).replace(/,/g, "")) || 0;
                const creditVariance = Number((sysCredits - bCredits).toFixed(2));
                const debitVariance = Number((sysDebits - bDebits).toFixed(2));

                return (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Card 1: System Credits */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{ p: 2.25, borderRadius: 2.5, border: "1px solid #CBD5E1", backgroundColor: "#F8FAFC", borderTop: "4px solid #059669" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                          1. System Total Credits (Inflows)
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#059669", mt: 0.5 }}>
                          ₦{fNumber(sysCredits)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748B" }}>
                          Total member deposits, shares &amp; repayments
                        </Typography>
                      </Paper>
                    </Grid>

                    {/* Card 2: Bank Statement Credits */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{ p: 2.25, borderRadius: 2.5, border: "1px solid #CBD5E1", backgroundColor: "#ffffff", borderTop: "4px solid #0B3B60" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                          2. Bank Statement Total Credits
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#0B3B60", mt: 0.5 }}>
                          ₦{fNumber(bCredits)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748B" }}>
                          Total deposits appearing on bank statement
                        </Typography>
                      </Paper>
                    </Grid>

                    {/* Card 3: Credit Variance */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{
                        p: 2.25,
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: creditVariance === 0 && bCredits > 0 ? "#86EFAC" : "#FECACA",
                        backgroundColor: creditVariance === 0 && bCredits > 0 ? "#F0FDF4" : "#FEF2F2",
                        borderTop: `4px solid ${creditVariance === 0 && bCredits > 0 ? "#16A34A" : "#DC2626"}`
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                          3. Credit Variance (Δ)
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: creditVariance === 0 && bCredits > 0 ? "#16A34A" : "#DC2626", mt: 0.5 }}>
                          {creditVariance >= 0 ? "+" : ""}₦{fNumber(creditVariance)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: creditVariance === 0 && bCredits > 0 ? "#16A34A" : "#DC2626", fontWeight: 700 }}>
                          {creditVariance === 0 && bCredits > 0 ? "✓ Perfectly Reconciled" : "Discrepancy in deposits"}
                        </Typography>
                      </Paper>
                    </Grid>

                    {/* Card 4: System Debits */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{ p: 2.25, borderRadius: 2.5, border: "1px solid #CBD5E1", backgroundColor: "#F8FAFC", borderTop: "4px solid #D97706" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                          4. System Total Debits (Outflows)
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#D97706", mt: 0.5 }}>
                          ₦{fNumber(sysDebits)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748B" }}>
                          Total loans disbursed, withdrawals &amp; expenses
                        </Typography>
                      </Paper>
                    </Grid>

                    {/* Card 5: Bank Statement Debits */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{ p: 2.25, borderRadius: 2.5, border: "1px solid #CBD5E1", backgroundColor: "#ffffff", borderTop: "4px solid #0B3B60" }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                          5. Bank Statement Total Debits
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: "#0B3B60", mt: 0.5 }}>
                          ₦{fNumber(bDebits)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748B" }}>
                          Total withdrawals &amp; charges on bank statement
                        </Typography>
                      </Paper>
                    </Grid>

                    {/* Card 6: Debit Variance */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper sx={{
                        p: 2.25,
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: debitVariance === 0 && bDebits > 0 ? "#86EFAC" : "#FECACA",
                        backgroundColor: debitVariance === 0 && bDebits > 0 ? "#F0FDF4" : "#FEF2F2",
                        borderTop: `4px solid ${debitVariance === 0 && bDebits > 0 ? "#16A34A" : "#DC2626"}`
                      }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                          6. Debit Variance (Δ)
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: debitVariance === 0 && bDebits > 0 ? "#16A34A" : "#DC2626", mt: 0.5 }}>
                          {debitVariance >= 0 ? "+" : ""}₦{fNumber(debitVariance)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: debitVariance === 0 && bDebits > 0 ? "#16A34A" : "#DC2626", fontWeight: 700 }}>
                          {debitVariance === 0 && bDebits > 0 ? "✓ Perfectly Reconciled" : "Discrepancy in disbursements"}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                );
              })()}

              {/* BANK STATEMENT DATA INPUT WORKSPACE */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: "1px solid #CBD5E1", backgroundColor: "#ffffff" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: "#0B3B60" }}>
                    Record Commercial Bank Statement Figures
                  </Typography>
                  <Chip
                    label="Bank: Jaiz Bank Plc (Non-Interest)"
                    size="small"
                    sx={{ backgroundColor: "#EFF6FF", color: "#1D4ED8", fontWeight: 700 }}
                  />
                </Box>

                {/* Official Mutual Trust Account Selector (from Landing Page) */}
                <Box sx={{ mb: 2.5, p: 2, borderRadius: 2, backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0" }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "#475569", display: "block", mb: 1 }}>
                    SELECT MUTUAL TRUST OFFICIAL BANK ACCOUNT (FROM LANDING PAGE):
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {OFFICIAL_MUTUAL_TRUST_ACCOUNTS.map((acct) => {
                      const isSelected = selectedOfficialAcct === acct.number;
                      return (
                        <Button
                          key={acct.number}
                          size="small"
                          variant={isSelected ? "contained" : "outlined"}
                          onClick={() => {
                            setSelectedOfficialAcct(acct.number);
                            setBankName("Jaiz Bank");
                            setBankAccountNumber(acct.number);
                          }}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.8rem",
                            py: 0.6,
                            px: 1.5,
                            backgroundColor: isSelected ? "#0B3B60" : "#ffffff",
                            borderColor: isSelected ? "#0B3B60" : "#CBD5E1",
                            color: isSelected ? "#ffffff" : "#1E293B",
                            boxShadow: isSelected ? "0 2px 6px rgba(11, 59, 96, 0.2)" : "none",
                            "&:hover": {
                              backgroundColor: isSelected ? "#07243B" : "#F1F5F9"
                            }
                          }}
                        >
                          {acct.title} ({acct.number})
                        </Button>
                      );
                    })}
                    <Button
                      size="small"
                      variant={selectedOfficialAcct === "custom" ? "contained" : "outlined"}
                      onClick={() => {
                        setSelectedOfficialAcct("custom");
                        setBankName("");
                        setBankAccountNumber("");
                      }}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        py: 0.6,
                        px: 1.5,
                        backgroundColor: selectedOfficialAcct === "custom" ? "#0D9488" : "#ffffff",
                        borderColor: selectedOfficialAcct === "custom" ? "#0D9488" : "#CBD5E1",
                        color: selectedOfficialAcct === "custom" ? "#ffffff" : "#0D9488",
                        "&:hover": {
                          backgroundColor: selectedOfficialAcct === "custom" ? "#0F766E" : "#F1F5F9"
                        }
                      }}
                    >
                      Custom / Write Bank Details
                    </Button>
                  </Box>
                </Box>

                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Bank Name *"
                      value={bankName}
                      onChange={(e) => {
                        setBankName(e.target.value);
                        if (selectedOfficialAcct !== "custom" && e.target.value !== "Jaiz Bank") {
                          setSelectedOfficialAcct("custom");
                        }
                      }}
                      placeholder="e.g. Jaiz Bank"
                      helperText="Default: Jaiz Bank (or write any bank name)"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Bank Account Number *"
                      value={bankAccountNumber}
                      onChange={(e) => {
                        setBankAccountNumber(e.target.value);
                        if (selectedOfficialAcct !== "custom" && e.target.value !== selectedOfficialAcct) {
                          setSelectedOfficialAcct("custom");
                        }
                      }}
                      placeholder="e.g. 0018169614"
                      helperText="Selected account number (or write any account)"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Statement Reference"
                      value={statementRef}
                      onChange={(e) => setStatementRef(e.target.value)}
                      placeholder="e.g. STMT-2026-10"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="Opening Balance (₦)"
                      value={bankOpeningBal}
                      onChange={(e) => setBankOpeningBal(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Bank Statement Total Credits (₦) *"
                      placeholder="Total deposits on statement"
                      value={bankInputCredits}
                      onChange={(e) => setBankInputCredits(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                        inputMode: "numeric"
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { fontSize: "1.1rem", fontWeight: 700 } }}
                      helperText="Total credit deposits recorded in the bank statement"
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Bank Statement Total Debits (₦) *"
                      placeholder="Total withdrawals on statement"
                      value={bankInputDebits}
                      onChange={(e) => setBankInputDebits(e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                        inputMode: "numeric"
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { fontSize: "1.1rem", fontWeight: 700 } }}
                      helperText="Total debit withdrawals and charges recorded in the bank statement"
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Reconciliation Audit Notes &amp; Variance Explanations"
                      placeholder="e.g. ₦150 bank SMS charge not yet posted to minutes ledger; all other deposits balanced."
                      value={bankReconNotes}
                      onChange={(e) => setBankReconNotes(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleSaveBankReconciliation}
                      disabled={savingBankRecon || !bankInputCredits || !bankInputDebits}
                      startIcon={savingBankRecon ? <CircularProgress size={18} sx={{ color: "#ffffff" }} /> : <CheckCircleRoundedIcon />}
                      sx={{
                        backgroundColor: "#0B3B60",
                        fontWeight: 800,
                        textTransform: "none",
                        px: 3.5,
                        py: 1.25,
                        borderRadius: "10px",
                        "&:hover": { backgroundColor: "#072740" }
                      }}
                    >
                      {savingBankRecon ? "Saving..." : "Save & Finalize Monthly Bank Reconciliation"}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* BREAKDOWN OF INTERNAL SYSTEM TRANSACTIONS */}
              {bankSummaryData?.systemFigures?.breakdown && (
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                  {/* Credits Breakdown */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #CBD5E1" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#059669", mb: 1.5 }}>
                        Internal Inflows (Credits) Breakdown
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          {Object.entries(bankSummaryData.systemFigures.breakdown.credits || {}).map(([acc, amt]) => (
                            <TableRow key={acc} hover>
                              <TableCell sx={{ textTransform: "capitalize", fontWeight: 600, color: "#334155" }}>
                                {acc.replace(/([A-Z])/g, " $1")}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800, color: "#059669" }}>
                                ₦{fNumber(amt)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>

                  {/* Debits Breakdown */}
                  <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #CBD5E1" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#D97706", mb: 1.5 }}>
                        Internal Outflows (Debits) Breakdown
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          {Object.entries(bankSummaryData.systemFigures.breakdown.debits || {}).map(([acc, amt]) => (
                            <TableRow key={acc} hover>
                              <TableCell sx={{ textTransform: "capitalize", fontWeight: 600, color: "#334155" }}>
                                {acc.replace(/([A-Z])/g, " $1")}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 800, color: "#D97706" }}>
                                ₦{fNumber(amt)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {/* PAST RECONCILIATIONS LOG */}
              {pastBankRecons.length > 0 && (
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #CBD5E1" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0B3B60", mb: 1.5 }}>
                    Historical Monthly Bank Reconciliations
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ backgroundColor: "#F1F5F9" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800 }}>Cycle Period</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Bank</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>Bank Credits</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>System Credits</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>Credit Δ</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>Bank Debits</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>System Debits</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>Debit Δ</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 800 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Reconciled By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pastBankRecons.map((r) => (
                          <TableRow key={r._id} hover>
                            <TableCell sx={{ fontWeight: 700 }}>{r.cyclePeriod}</TableCell>
                            <TableCell>{r.bankName || "Commercial Bank"}</TableCell>
                            <TableCell align="right">₦{fNumber(r.bankStatementCredits)}</TableCell>
                            <TableCell align="right">₦{fNumber(r.systemCredits)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: r.creditVariance === 0 ? "#16A34A" : "#DC2626" }}>
                              ₦{fNumber(r.creditVariance)}
                            </TableCell>
                            <TableCell align="right">₦{fNumber(r.bankStatementDebits)}</TableCell>
                            <TableCell align="right">₦{fNumber(r.systemDebits)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: r.debitVariance === 0 ? "#16A34A" : "#DC2626" }}>
                              ₦{fNumber(r.debitVariance)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={r.status === 'balanced' ? "BALANCED" : "VARIANCE"}
                                size="small"
                                sx={{
                                  fontWeight: 800,
                                  fontSize: "0.7rem",
                                  backgroundColor: r.status === 'balanced' ? "#DCFCE7" : "#FEE2E2",
                                  color: r.status === 'balanced' ? "#15803D" : "#B91C1C"
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: "0.85rem" }}>{r.reconcilerName || "Financial Officer"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </>
          )}
        </Box>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: STAMP DUTY DEDUCTIONS & WAIVERS (FINANCIAL I, II, III, PRESIDENT, ADMIN) */}
      {/* ========================================================================= */}
      {mainPageTab === 2 && (
        <Box>
          {/* Executive Stamp Duty Cards */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #CBD5E1", backgroundColor: "#F0FDF4", borderTop: "4px solid #16A34A" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#166534", textTransform: "uppercase" }}>
                  Total Stamp Duty Deducted
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, color: "#15803D", mt: 0.5 }}>
                  ₦{fNumber(stampSummary.totalStampDutyDeducted || 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: "#166534" }}>
                  Accumulated from {stampSummary.totalChargedCount || 0} qualifying deposits
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #CBD5E1", backgroundColor: "#EFF6FF", borderTop: "4px solid #2563EB" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#1E40AF", textTransform: "uppercase" }}>
                  Total Deposit Volume
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, color: "#1D4ED8", mt: 0.5 }}>
                  ₦{fNumber(stampSummary.totalDepositVolume || 0)}
                </Typography>
                <Typography variant="caption" sx={{ color: "#1E40AF" }}>
                  Across all member deposit transactions
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #CBD5E1", backgroundColor: "#FFFBEB", borderTop: "4px solid #D97706" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#92400E", textTransform: "uppercase" }}>
                  Total Waived Deposits
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, color: "#B45309", mt: 0.5 }}>
                  {stampSummary.totalWaivedCount || 0}
                </Typography>
                <Typography variant="caption" sx={{ color: "#92400E" }}>
                  Preserved ₦{fNumber(stampSummary.totalWaivedPotentialAmount || 0)} for members
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2.5, borderRadius: 2.5, border: "1px solid #CBD5E1", backgroundColor: "#FAF5FF", borderTop: "4px solid #7C3AED" }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#6B21A8", textTransform: "uppercase" }}>
                  Stamp Duty Reserve Pool
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, color: "#7E22CE", mt: 0.5 }}>
                  Active Pool
                </Typography>
                <Typography variant="caption" sx={{ color: "#6B21A8" }}>
                  Audited by Financial Officers &amp; President
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Action Bar & Filters */}
          <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2.5, border: "1px solid #CBD5E1", backgroundColor: "#ffffff" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search member name, reg no, ref..."
                  value={stampSearch}
                  onChange={(e) => setStampSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "#94A3B8" }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{ width: 260 }}
                />

                <TextField
                  select
                  size="small"
                  label="Account"
                  value={stampAccountFilter}
                  onChange={(e) => setStampAccountFilter(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ width: 140 }}
                >
                  <option value="all">All Accounts</option>
                  <option value="savings">Savings</option>
                  <option value="shares">Shares</option>
                  <option value="loan">Loan Repayment</option>
                  <option value="split">Split Deposit</option>
                  <option value="ileya">Ileya</option>
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Waiver Status"
                  value={stampWaivedFilter}
                  onChange={(e) => setStampWaivedFilter(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ width: 150 }}
                >
                  <option value="all">All Transactions</option>
                  <option value="false">Deducted Only</option>
                  <option value="true">Waived Only</option>
                </TextField>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={loadStampDuty}
                  sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 700 }}
                >
                  Filter
                </Button>
              </Box>

              {canShowSplitTool && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    onClick={() => setSplitDepositOpen(true)}
                    startIcon={<CallSplitRoundedIcon />}
                    sx={{
                      backgroundColor: "#0D9488",
                      fontWeight: 800,
                      textTransform: "none",
                      borderRadius: "8px",
                      "&:hover": { backgroundColor: "#0F766E" }
                    }}
                  >
                    Deposit Split Tool (Savings/Shares/Loan/Ileya/Takaful)
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setDevMinWelSplitOpen(true)}
                    startIcon={<VolunteerActivismRoundedIcon />}
                    sx={{
                      borderColor: "#0D9488",
                      color: "#0D9488",
                      fontWeight: 800,
                      textTransform: "none",
                      borderRadius: "8px",
                      bgcolor: "#F0FDF4",
                      "&:hover": { bgcolor: "#E6FFFA", borderColor: "#0F766E" }
                    }}
                  >
                    Welfare &amp; Dues Split Tool (Wel/Min/Dev)
                  </Button>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Stamp Duty Ledger Table */}
          {loadingStamp ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#0D9488" }} />
            </Box>
          ) : stampRecords.length === 0 ? (
            <Paper sx={{ p: 5, textAlign: "center", borderRadius: 3, border: "1px solid #CBD5E1" }}>
              <Typography sx={{ color: "#64748B", fontWeight: 600 }}>
                No stamp duty records found matching the selected filters.
              </Typography>
            </Paper>
          ) : (
            <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #CBD5E1", overflow: "hidden" }}>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F1F5F9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Member Name</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Account Credited</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>Deposit Amount (₦)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>Stamp Duty (₦)</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Reference / Notes</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Recorded By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stampRecords.map((rec) => (
                      <TableRow key={rec._id} hover>
                        <TableCell sx={{ fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                          {rec.date ? new Date(rec.date).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {rec.memberName || "Member"}
                          {rec.memberRegNo && <Typography variant="caption" sx={{ display: "block", color: "#64748B" }}>#{rec.memberRegNo}</Typography>}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={rec.accountType || "deposit"}
                            size="small"
                            sx={{
                              textTransform: "uppercase",
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              backgroundColor: rec.accountType === "split" ? "#ECFDF5" : "#EFF6FF",
                              color: rec.accountType === "split" ? "#065F46" : "#1E40AF"
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          ₦{fNumber(rec.depositAmount)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: rec.isWaived ? "#94A3B8" : "#059669" }}>
                          {rec.isWaived ? "₦0.00" : `₦${fNumber(rec.stampDutyAmount)}`}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={rec.isWaived ? "WAIVED" : "DEDUCTED"}
                            size="small"
                            sx={{
                              fontWeight: 800,
                              fontSize: "0.7rem",
                              backgroundColor: rec.isWaived ? "#FEF3C7" : "#DCFCE7",
                              color: rec.isWaived ? "#92400E" : "#15803D"
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8rem", color: "#64748B", maxWidth: 220 }}>
                          {rec.isWaived ? (rec.waiveReason || "Officer waiver") : (rec.referenceNo || rec.notes || "Standard EMTL stamp duty")}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", color: "#334155" }}>
                          {rec.recorderName || "Financial Officer"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Split Deposit Modal (Savings/Shares/Loan/Ileya/Takaful) */}
          <SplitDepositModal
            open={splitDepositOpen}
            onClose={() => setSplitDepositOpen(false)}
            onSuccess={loadStampDuty}
          />

          {/* Welfare, Minutes & Development Split Modal */}
          <DevMinWelSplitModal
            open={devMinWelSplitOpen}
            onClose={() => setDevMinWelSplitOpen(false)}
            onSuccess={loadStampDuty}
          />
        </Box>
      )}

      {/* ========================================================================= */}
      {/* DIALOG: Record Account Reconciliation Adjustment or Inter-Account Transfer*/}
      {/* ========================================================================= */}
      <Dialog
        open={dialogOpen}
        onClose={() => !submitting && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
        PaperProps={{ sx: { borderRadius: 3, p: { xs: 0.5, sm: 1 }, m: { xs: 1.5, sm: 3 } } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#0B3B60", pb: 1, fontSize: { xs: "1.15rem", sm: "1.25rem" } }}>
          Record Account Reconciliation
        </DialogTitle>

        <DialogContent sx={{ pt: 1, px: { xs: 1.5, sm: 3 } }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2.5 }}>
            <Tabs
              value={reconTab}
              onChange={(e, val) => {
                setReconTab(val);
                setDialogError("");
              }}
              variant="fullWidth"
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  minHeight: { xs: 44, sm: 48 },
                  px: { xs: 1, sm: 2 }
                },
                "& .Mui-selected": { color: "#0B3B60" },
                "& .MuiTabs-indicator": { backgroundColor: "#0B3B60", height: 3 }
              }}
            >
              <Tab icon={<SwapHorizIcon fontSize="small" />} iconPosition="start" label="Inter-Account Transfer" />
              <Tab icon={<AccountBalanceIcon fontSize="small" />} iconPosition="start" label="External Purpose / Source" />
            </Tabs>
          </Box>

          {dialogError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {dialogError}
            </Alert>
          )}

          <form onSubmit={handleCreateSubmit} id="reconciliation-form">
            <Stack spacing={2.5}>
              {/* TAB 0: INTER-ACCOUNT TRANSFER */}
              {reconTab === 0 ? (
                <>
                  <Alert severity="info" icon={<CompareArrowsIcon />} sx={{ borderRadius: 2 }}>
                    <AlertTitle sx={{ fontWeight: 700 }}>Balanced Double-Entry Transfer</AlertTitle>
                    Move funds from one cooperative account to another (e.g. from Shares to Business capitalisation). This automatically deducts from the source account and credits the destination account while preserving overall cooperative fund balance.
                  </Alert>

                  <Grid container spacing={2}>
                    {/* Source Account */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel id="from-account-label">Transfer From (Debit/Deduct)</InputLabel>
                        <Select
                          labelId="from-account-label"
                          label="Transfer From (Debit/Deduct)"
                          value={formTransferFrom}
                          onChange={(e) => setFormTransferFrom(e.target.value)}
                        >
                          {visibleAccounts.map((a) => (
                            <MenuItem key={a.key} value={a.key} disabled={a.key === formTransferTo}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: a.color }} />
                                <span>Total {a.label}</span>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Destination Account */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel id="to-account-label">Transfer To (Credit/Add)</InputLabel>
                        <Select
                          labelId="to-account-label"
                          label="Transfer To (Credit/Add)"
                          value={formTransferTo}
                          onChange={(e) => setFormTransferTo(e.target.value)}
                        >
                          {visibleAccounts.map((a) => (
                            <MenuItem key={a.key} value={a.key} disabled={a.key === formTransferFrom}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: a.color }} />
                                <span>Total {a.label}</span>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Transfer Visual Double-Entry Preview Card - Responsive vertical stack on mobile */}
                  <Paper
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 2.5,
                      backgroundColor: "#F1F5F9",
                      border: "1px dashed #94A3B8",
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 800, color: "#475569", textTransform: "uppercase", display: "block", mb: 1 }}>
                      Double-Entry Preview:
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: { xs: 1.5, sm: 1 },
                      }}
                    >
                      <Box sx={{ textAlign: "center", p: 1.5, backgroundColor: "#FFFFFF", borderRadius: 2, width: { xs: "100%", sm: "auto" }, flex: 1, border: "1px solid #E2E8F0" }}>
                        <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>SOURCE (DEBIT)</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: fromAccObj.color }}>
                          Total {fromAccObj.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#DC2626", fontWeight: 700 }}>
                          -₦{formAmount ? fNumber(parseFloat(String(formAmount).replace(/,/g, "")) || 0) : "0.00"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: { xs: "row", sm: "column" }, alignItems: "center", gap: 0.5, px: 1 }}>
                        <SwapHorizIcon
                          sx={{
                            color: "#0B3B60",
                            fontSize: "2rem",
                            transform: { xs: "rotate(90deg)", sm: "none" },
                            transition: "transform 0.2s ease"
                          }}
                        />
                        <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, fontSize: "0.7rem" }}>
                          TRANSFER
                        </Typography>
                      </Box>

                      <Box sx={{ textAlign: "center", p: 1.5, backgroundColor: "#FFFFFF", borderRadius: 2, width: { xs: "100%", sm: "auto" }, flex: 1, border: "1px solid #E2E8F0" }}>
                        <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700 }}>DESTINATION (CREDIT)</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: toAccObj.color }}>
                          Total {toAccObj.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#059669", fontWeight: 700 }}>
                          +₦{formAmount ? fNumber(parseFloat(String(formAmount).replace(/,/g, "")) || 0) : "0.00"}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" sx={{ color: "#64748B", display: "block", mt: 1, textAlign: "center" }}>
                      Net Cooperative Grand Total Impact: ₦0.00 (Fund Neutral)
                    </Typography>
                  </Paper>
                </>
              ) : (
                <>
                  {/* TAB 1: EXTERNAL PURPOSE / OUTSIDE TRANSACTION */}
                  <Alert severity="info" icon={<CategoryIcon />} sx={{ borderRadius: 2 }}>
                    <AlertTitle sx={{ fontWeight: 700 }}>External Inflow / Outflow</AlertTitle>
                    Record financial adjustments for purposes outside cooperative accounts (e.g. bank charges, stationeries, legal fees, or non-interest investment returns).
                  </Alert>

                  {/* Target Cooperative Account */}
                  <FormControl fullWidth required>
                    <InputLabel id="target-account-label">Cooperative Account Impacted</InputLabel>
                    <Select
                      labelId="target-account-label"
                      label="Cooperative Account Impacted"
                      value={formAccount}
                      onChange={(e) => setFormAccount(e.target.value)}
                    >
                      {visibleAccounts.map((a) => (
                        <MenuItem key={a.key} value={a.key}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: a.color }} />
                            <span>Total {a.label}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Action Type: Add vs Subtract */}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#334155", display: "block", mb: 1 }}>
                      External Flow Direction:
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant={formAction === "subtract" ? "contained" : "outlined"}
                          startIcon={<RemoveCircleOutlineIcon />}
                          onClick={() => handleActionChange("subtract")}
                          sx={{
                            py: 1.2,
                            px: { xs: 1, sm: 2 },
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                            backgroundColor: formAction === "subtract" ? "#DC2626" : "transparent",
                            borderColor: formAction === "subtract" ? "#DC2626" : "#CBD5E1",
                            color: formAction === "subtract" ? "#FFFFFF" : "#334155",
                            "&:hover": {
                              backgroundColor: formAction === "subtract" ? "#B91C1C" : "#FEF2F2",
                              borderColor: "#DC2626",
                            },
                          }}
                        >
                          Deduct / Outflow (-)
                        </Button>
                      </Grid>

                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant={formAction === "add" ? "contained" : "outlined"}
                          startIcon={<AddCircleOutlineIcon />}
                          onClick={() => handleActionChange("add")}
                          sx={{
                            py: 1.2,
                            px: { xs: 1, sm: 2 },
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                            backgroundColor: formAction === "add" ? "#059669" : "transparent",
                            borderColor: formAction === "add" ? "#059669" : "#CBD5E1",
                            color: formAction === "add" ? "#FFFFFF" : "#334155",
                            "&:hover": {
                              backgroundColor: formAction === "add" ? "#047857" : "#ECFDF5",
                              borderColor: "#059669",
                            },
                          }}
                        >
                          Credit / Inflow (+)
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* External Category Selection */}
                  <FormControl fullWidth required>
                    <InputLabel id="category-label">External Purpose / Category</InputLabel>
                    <Select
                      labelId="category-label"
                      label="External Purpose / Category"
                      value={
                        (formAction === "subtract" ? EXTERNAL_DEDUCTION_CATEGORIES : EXTERNAL_CREDIT_CATEGORIES).includes(formExternalCategory)
                          ? formExternalCategory
                          : (formAction === "subtract" ? EXTERNAL_DEDUCTION_CATEGORIES[0] : EXTERNAL_CREDIT_CATEGORIES[0])
                      }
                      onChange={(e) => setFormExternalCategory(e.target.value)}
                    >
                      {(formAction === "subtract" ? EXTERNAL_DEDUCTION_CATEGORIES : EXTERNAL_CREDIT_CATEGORIES).map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {/* SHARED FIELDS: Amount */}
              <Box>
                <TextField
                  fullWidth
                  required
                  label="Amount (₦)"
                  placeholder="e.g. 50,000"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                  }}
                  helperText="Enter the transaction amount"
                />
                <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                  {[10000, 20000, 50000, 100000, 250000, 500000].map((quick) => (
                    <Chip
                      key={quick}
                      label={`₦${fNumber(quick)}`}
                      size="small"
                      onClick={() => setQuickAmount(quick)}
                      sx={{ cursor: "pointer", fontWeight: 600, fontSize: "0.75rem" }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Transaction Date */}
              <TextField
                fullWidth
                required
                type="date"
                label="Date of Transaction"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Specify the calendar date when this transaction took place"
              />

              {/* Purpose / Description */}
              <TextField
                fullWidth
                required
                multiline
                rows={2}
                label="Transaction Purpose / Narration"
                placeholder={reconTab === 0 ? "e.g. Transfer 50,000 from Shares for Business capitalisation" : "e.g. Deduct 10,000 for secretariat stationaries or Q3 commercial bank charges"}
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                helperText="Provide a clear, detailed narration for the cooperative financial audit"
              />

              {/* Internal Reference / Voucher */}
              <TextField
                fullWidth
                label="Voucher / Reference / Bank Slip No (Optional)"
                placeholder="e.g. Voucher #MT-2026-081"
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
              />
            </Stack>
          </form>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            form="reconciliation-form"
            variant="contained"
            disabled={submitting}
            sx={{
              backgroundColor: reconTab === 0 ? "#0B3B60" : formAction === "add" ? "#059669" : "#DC2626",
              "&:hover": {
                backgroundColor: reconTab === 0 ? "#072740" : formAction === "add" ? "#047857" : "#B91C1C"
              },
              fontWeight: 700,
              textTransform: "none",
              px: 3,
            }}
          >
            {submitting ? "Processing..." : reconTab === 0 ? "Execute Inter-Account Transfer" : formAction === "add" ? "Record Credit Inflow" : "Record Deduction Outflow"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================================= */}
      {/* DIALOG: Delete / Revert Confirmation                                      */}
      {/* ========================================================================= */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        disableRestoreFocus
        PaperProps={{ sx: { borderRadius: 3, p: { xs: 0.5, sm: 1 }, m: { xs: 1.5, sm: 3 } } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#DC2626" }}>
          Revert Reconciliation Record?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#334155", mb: 1 }}>
            Are you sure you want to delete and revert this reconciliation entry?
          </Typography>
          {deleteTarget && (
            <>
              {deleteTarget.transferGroupId ? (
                <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
                  <AlertTitle sx={{ fontWeight: 700 }}>Linked Inter-Account Transfer</AlertTitle>
                  This entry is part of a linked transfer group (<code>{deleteTarget.transferGroupId}</code>).
                  Reverting this entry will <strong>automatically delete BOTH legs</strong> (source and destination) to maintain a balanced cooperative ledger.
                </Alert>
              ) : null}

              <Box sx={{ p: 1.5, backgroundColor: "#FEF2F2", borderRadius: 2, border: "1px solid #FECACA", mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#991B1B" }}>
                  {deleteTarget.action === "add" ? "+ Credit" : "- Deduction"} ₦{fNumber(deleteTarget.amount)}
                </Typography>
                <Typography variant="caption" sx={{ color: "#7F1D1D", display: "block" }}>
                  Account: Total {ACCOUNTS.find((a) => a.key === deleteTarget.accountType)?.label || deleteTarget.accountType}
                </Typography>
                {deleteTarget.externalCategory && (
                  <Typography variant="caption" sx={{ color: "#7F1D1D", display: "block" }}>
                    Category: {deleteTarget.externalCategory}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: "#7F1D1D", display: "block", mt: 0.5 }}>
                  Purpose: "{deleteTarget.description}"
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleting}
            onClick={handleDeleteConfirm}
            sx={{ fontWeight: 700, textTransform: "none" }}
          >
            {deleting ? "Reverting..." : deleteTarget?.transferGroupId ? "Revert Both Legs" : "Confirm Revert"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
